import { FC, useMemo } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@utils/cn';

export interface PasswordRule {
  key: string;
  label: string;
  test: (value: string) => boolean;
}

/**
 * Standard password rules shared across the auth forms. The special character
 * rule matches the set allowed by the Yup regex: @ $ ! % * ? & #
 */
export const PASSWORD_RULES: PasswordRule[] = [
  { key: 'length', label: 'At least 8 characters', test: (value) => value.length >= 8 },
  {
    key: 'uppercase',
    label: 'One uppercase letter (A-Z)',
    test: (value) => /[A-Z]/.test(value),
  },
  {
    key: 'lowercase',
    label: 'One lowercase letter (a-z)',
    test: (value) => /[a-z]/.test(value),
  },
  { key: 'number', label: 'One number (0-9)', test: (value) => /\d/.test(value) },
  {
    key: 'special',
    label: 'One special character (@ $ ! % * ? & #)',
    test: (value) => /[@$!%*?&#]/.test(value),
  },
];

export interface PasswordRequirementsProps {
  /** Current password value being validated. */
  value: string;
  /** Rule set to evaluate. Defaults to the standard rules. */
  rules?: PasswordRule[];
  /**
   * Force visibility. When omitted, the box appears automatically once the
   * user starts typing (value is not empty).
   */
  visible?: boolean;
  className?: string;
}

export const PasswordRequirements: FC<PasswordRequirementsProps> = ({
  value,
  rules = PASSWORD_RULES,
  visible,
  className,
}) => {
  const shouldShow = visible ?? value.length > 0;

  const checks = useMemo(
    () => rules.map((rule) => ({ ...rule, met: rule.test(value) })),
    [rules, value],
  );

  if (!shouldShow) return null;

  const allMet = checks.every((item) => item.met);

  return (
    <div
      className={cn(
        'rounded-lg border p-3 bg-gray-50 dark:bg-neutral-800/40',
        allMet
          ? 'border-green-300 dark:border-green-700/40'
          : 'border-gray-200 dark:border-neutral-700',
        className,
      )}>
      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
        Your password must contain:
      </p>
      <ul className="space-y-1">
        {checks.map((item) => (
          <li key={item.key} className="flex items-center gap-2 text-xs">
            {item.met ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 text-green-600 dark:text-green-400" />
            ) : (
              <Circle className="w-4 h-4 shrink-0 text-gray-400 dark:text-gray-500" />
            )}
            <span
              className={cn(
                item.met
                  ? 'font-medium text-green-700 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400',
              )}>
              {item.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PasswordRequirements;
